// Route permission mapping for hierarchical access control
export interface RoutePermission {
  route: string;
  childPermission: string;
  parentPermission?: string;
}

// Define the hierarchical permission mapping based on menu structure
export const routePermissions: RoutePermission[] = [
  // Compliance Management - Parent: "Compliance Mngt"
  {
    route: '/customer-compliance',
    childPermission: 'Customer Compliance',
    parentPermission: 'Compliance Mngt'
  },
  {
    route: '/merchant-compliance',
    childPermission: 'Merchant Compliance',
    parentPermission: 'Compliance Mngt'
  },
  {
    route: '/business-compliance',
    childPermission: 'Business Compliance',
    parentPermission: 'Compliance Mngt'
  },
  
  // System Management - Parent: "System Mngt"
  {
    route: '/system-management/delivery-rates',
    childPermission: 'Delivery Rates',
    parentPermission: 'System Mngt'
  },
  {
    route: '/delivery-rates',
    childPermission: 'Delivery Rates',
    parentPermission: 'System Mngt'
  },
  {
    route: '/system-management/exchange-rates',
    childPermission: 'Exchange Rates',
    parentPermission: 'System Mngt'
  },
  {
    route: '/system-management/service-fees',
    childPermission: 'Service Fees',
    parentPermission: 'System Mngt'
  },
  {
    route: '/system-management/ticket-settings',
    childPermission: 'Ticket Settings',
    parentPermission: 'System Mngt'
  },
  {
    route: '/system-management/user-management',
    childPermission: 'User Mngt',
    parentPermission: 'System Mngt'
  },
  {
    route: '/system-management/user',
    childPermission: 'User Mngt',
    parentPermission: 'System Mngt'
  },
  {
    route: '/system-management/roles-and-permissions',
    childPermission: 'Roles & Permissions',
    parentPermission: 'System Mngt'
  },
  {
    route: '/system-management/cashback-rules',
    childPermission: 'Cashback Rules',
    parentPermission: 'System Mngt'
  },
  {
    route: '/financial-management/banks',
    childPermission: 'Bank Accounts',
    parentPermission: 'Float Requests'
  },
  {
    route: '/chart-of-accounts',
    childPermission: 'Chart of Accounts',
    parentPermission: 'System Mngt'
  },
  {
    route: '/ecommerce/products',
    childPermission: 'ecommerce.products',
    parentPermission: 'ecommerce'
  },
  {
    route: '/ecommerce/orders',
    childPermission: 'ecommerce.orders',
    parentPermission: 'ecommerce'
  },
  {
    route: '/ecommerce/refund-requests',
    childPermission: 'ecommerce.refund-requests',
    parentPermission: 'ecommerce'
  },
  {
    route: '/system-management/shift-management',
    childPermission: 'Shift Management',
    parentPermission: 'System Mngt'
  },
  
  // Delivery Services - Parent: "Delivery Services"
  {
    route: '/delivery-management/deliveries',
    childPermission: 'Delivery Transactions',
    parentPermission: 'Delivery Services'
  },
  {
    route: '/delivery-management/manage-agents',
    childPermission: 'Manage Delivery Agents',
    parentPermission: 'Delivery Services'
  },
  {
    route: '/delivery-management/delivery-compliance',
    childPermission: 'Delivery Compliance',
    parentPermission: 'Delivery Services'
  },
  {
    route: '/delivery-management/delivery',
    childPermission: 'Delivery Transactions',
    parentPermission: 'Delivery Services'
  },
  {
    route: '/delivery-management/agent',
    childPermission: 'Manage Delivery Agents',
    parentPermission: 'Delivery Services'
  },
  
  // Add more routes as needed based on your menu structure
];

/**
 * Get route permission info for a given path
 */
export function getRoutePermission(pathname: string): RoutePermission | null {
  // Normalize the pathname (remove leading slash if present)
  const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
  
  return routePermissions.find(rp => {
    // Check for exact match first
    if (rp.route === normalizedPath) {
      return true;
    }
    
    // Check if the current path starts with the route (for sub-paths)
    if (normalizedPath.startsWith(rp.route + '/') || normalizedPath.startsWith(rp.route + '?')) {
      return true;
    }
    
    return false;
  }) || null;
}

/**
 * Check if a route requires hierarchical permission validation
 */
export function requiresHierarchicalPermission(pathname: string): boolean {
  const routePermission = getRoutePermission(pathname);
  return routePermission?.parentPermission !== undefined;
}
